import React, { useState, useEffect } from 'react';
import { FiSave, FiPenTool, FiType, FiSun, FiMoon, FiLayout, FiSliders } from 'react-icons/fi';
import { HiOutlineCheck, HiOutlineArrowSmRight, HiOutlineRefresh, HiOutlineRewind } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { HexColorPicker } from 'react-colorful';
import { applyTheme, saveThemeSettings, loadThemeSettings, applyDefaultTheme } from '../../../../services/themeService';

// تعريف مجموعات الإعدادات - يمكن نقل هذا إلى ملف منفصل
const settingsGroups = {
  appearance: {
    id: 'appearance',
    title: 'المظهر العام',
    icon: FiLayout,
    settings: {
      theme: {
        type: 'select',
        label: 'الثيم',
        icon: FiMoon,
        options: [
          { value: 'light', label: 'فاتح' },
          { value: 'dark', label: 'داكن' },
          { value: 'system', label: 'حسب النظام' }
        ],
        default: 'light'
      },
      direction: {
        type: 'select',
        label: 'اتجاه الموقع',
        icon: FiType,
        options: [
          { value: 'rtl', label: 'من اليمين إلى اليسار (RTL)' },
          { value: 'ltr', label: 'من اليسار إلى اليمين (LTR)' }
        ],
        default: 'rtl'
      },
      primaryColor: {
        type: 'color',
        label: 'اللون الرئيسي',
        icon: FiSliders,
        default: '#4F46E5'
      },
      font: {
        type: 'select',
        label: 'الخط',
        icon: FiType,
        options: [
          { value: 'cairo', label: 'Cairo' },
          { value: 'tajawal', label: 'Tajawal' },
          { value: 'ibm-plex-sans-arabic', label: 'IBM Plex Sans Arabic' }
        ],
        default: 'cairo'
      }
    }
  },
  advanced: {
    id: 'advanced',
    title: 'التخصيص المتقدم',
    icon: FiSliders,
    settings: {
      fontSize: {
        type: 'select',
        label: 'حجم الخط',
        options: [
          { value: 'small', label: 'صغير' },
          { value: 'medium', label: 'متوسط' },
          { value: 'large', label: 'كبير' }
        ],
        default: 'medium'
      },
      borderRadius: {
        type: 'select',
        label: 'نصف قطر الحواف',
        options: [
          { value: 'none', label: 'بدون' },
          { value: 'small', label: 'صغير' },
          { value: 'medium', label: 'متوسط' },
          { value: 'large', label: 'كبير' }
        ],
        default: 'medium'
      },
      customCSS: {
        type: 'textarea',
        label: 'CSS مخصص',
        placeholder: 'أدخل CSS مخصص هنا...',
        description: 'سيتم تطبيق هذا CSS على جميع صفحات الموقع',
        default: ''
      }
    }
  }
  // يمكن إضافة مجموعات جديدة هنا
};

const PersonalizationSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [previousSettings, setPreviousSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    // محاولة تحميل الإعدادات المحفوظة
    const savedSettings = loadThemeSettings();
    if (savedSettings) {
      setSettings(savedSettings);
      setPreviousSettings(savedSettings); // حفظ النسخة السابقة
      applyTheme(savedSettings);
    } else {
      // تطبيق الإعدادات الافتراضية إذا لم تكن هناك إعدادات محفوظة
      const defaultSettings = applyDefaultTheme();
      setSettings(defaultSettings);
      setPreviousSettings(defaultSettings);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPreviousSettings(settings); // حفظ النسخة السابقة قبل التغيير
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // حفظ الإعدادات في التخزين المحلي
      saveThemeSettings(settings);
      
      // تطبيق التغييرات مباشرة
      applyTheme(settings);
      
      // في المستقبل: إرسال الإعدادات إلى API
      // await api.patch('/api/settings/personalization', settings);
      // Save personalization settings
    } catch (error) {
      // Handle settings save error
    } finally {
      setIsLoading(false);
    }
  };

  const handleUndo = () => {
    if (previousSettings) {
      setSettings(previousSettings);
      applyTheme(previousSettings);
    }
  };

  const handleReset = () => {
    const defaultSettings = applyDefaultTheme();
    setPreviousSettings(settings);
    setSettings(defaultSettings);
    applyTheme(defaultSettings);
  };

  // مكون لعرض حقل إعداد واحد
  const renderSetting = (settingId, settingConfig) => {
    switch (settingConfig.type) {
      case 'select':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              <div className="flex items-center gap-2">
                {settingConfig.icon && <settingConfig.icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                <span>{settingConfig.label}</span>
              </div>
            </label>
            <select
              name={settingId}
              value={settings[settingId]}
              onChange={handleInputChange}
              className="input-base"
            >
              {settingConfig.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'color':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              <div className="flex items-center gap-2">
                {settingConfig.icon && <settingConfig.icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                <span>{settingConfig.label}</span>
              </div>
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="input-base flex items-center gap-2"
              >
                <div
                  className="w-6 h-6 rounded-full border border-gray-300"
                  style={{ backgroundColor: settings[settingId] }}
                />
                <span>{settings[settingId]}</span>
              </button>
              {showColorPicker && (
                <div className="absolute z-10 mt-2">
                  <HexColorPicker
                    color={settings[settingId]}
                    onChange={(color) => setSettings(prev => ({ ...prev, [settingId]: color }))}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              {settingConfig.label}
            </label>
            <textarea
              name={settingId}
              value={settings[settingId]}
              onChange={handleInputChange}
              rows="4"
              className="input-base"
              placeholder={settingConfig.placeholder}
            />
            {settingConfig.description && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {settingConfig.description}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">المظهر العام</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/admin/dashboard/settings')}
              className="group relative p-1 text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <HiOutlineArrowSmRight className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                رجوع للإعدادات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
            <button
              onClick={handleUndo}
              className="group relative p-1 text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <HiOutlineRewind className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                تراجع عن آخر تغيير
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
            <button
              onClick={handleReset}
              className="group relative p-1 text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <HiOutlineRefresh className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                إعادة للوضع الافتراضي
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="group relative p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-500 dark:hover:text-emerald-400"
            >
              <HiOutlineCheck className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                {isLoading ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(settingsGroups).map(([groupId, group]) => (
              <div key={groupId} className="space-y-4">
                <div className="flex items-center space-x-4 space-x-reverse mb-4">
                  <div className={`p-2 ${
                    groupId === 'appearance' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
                  } rounded-lg`}>
                    <group.icon className={`w-6 h-6 ${
                      groupId === 'appearance' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'
                    }`} />
                  </div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-white">{group.title}</h3>
                </div>
                <div className="grid gap-4">
                  {Object.entries(group.settings).map(([settingId, setting]) => (
                    <div key={settingId} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                      {renderSetting(settingId, setting)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalizationSettings;
